import { Search, SearchControlled } from '@app/components/Search';
import {
  HStack,
  VStack,
  Text,
  Grid,
  GridItem,
  Box,
  Button
} from '@chakra-ui/react';
import { DropdownMenu } from '@app/components/DropdownMenu';
import React, { FC } from 'react';
import KimLogo from '@app/assets/images/logo/kimLogo.svg';
import s from './SpNFTFilter.module.scss';
import {
  Arbitrum,
  Ethereum,
  Linea,
  MantaPacific,
  Mantle,
  Mode,
  Polygon
} from '@thirdweb-dev/chains';
import { NetworkIcon } from '@app/components/NetworkIcon';
import { useNftMarketplaceFiltersState } from '@app/state/nftMarketplaceFiltersStore';
import {
  AUCTION_STATUS_OPTIONS,
  AUCTION_TYPE_OPTIONS
} from '@app/screens/nft-marketplace/SpNFTFilter/helpers';
import { PremiumListingsFilter } from '@app/screens/nft-marketplace/SpNFTFilter/PremiumListingsFilter';

const PRICE_AND_LISTING_OPTIONS = [
  { title: 'Price low to high', value: 'asc' },
  { title: 'Price high to low', value: 'desc' }
  // { title: 'Recently listed', value: '2' },
  // { title: 'Recently sold', value: '3' },
  // { title: 'Ending soon', value: '4' }
];

const VAULT_OPTIONS = [
  {
    title: 'All',
    value: ''
  },
  {
    title: 'Steer',
    value: 'steer'
  },
  {
    title: 'Ichi',
    value: 'ichi'
  },
  {
    title: 'Gamma',
    value: 'gamma'
  }
];

//@Todo Add the supported networks

const NETWORKS = [
  Arbitrum,
  Ethereum,
  Polygon,
  Linea,
  Mantle,
  MantaPacific,
  Mode
];

const mapNetworksToSelect = () => {
  return NETWORKS.map(network => {
    return {
      title: network.name,
      icon: (
        <NetworkIcon
          key={network.networkId}
          name={network?.name}
          iconUrl={network?.icon?.url.replace(
            'ipfs://',
            'https://ipfs.io/ipfs/'
          )}
        />
      ),
      value: network.networkId.toString()
    };
  });
};

type Props = {
  isMobile?: boolean;
};

export const SpNFTFilter: FC<Props> = ({ isMobile = false }) => {
  const {
    vault,
    searchTerm,
    dex,
    chain,
    sortOrder,
    auctionType,
    auctionStatus,
    actions: {
      setSearchTerm,
      setVault,
      setDex,
      setChain,
      setSortOrder,
      setAuctionType,
      setAuctionStatus,
      setActivePage
    }
  } = useNftMarketplaceFiltersState();

  return (
    <>
      <PremiumListingsFilter />
      {isMobile ? (
        <VStack paddingTop="18px" alignItems="center" gap="16px" mb={6}>
          <HStack width="100%" paddingTop="32px">
            <SearchControlled
              onSearch={val => {
                setActivePage(0);
                setSearchTerm(val);
              }}
              value={searchTerm}
            />
            <VStack gap="8px" alignItems="flex-start">
              <DropdownMenu
                disabled
                iconPosition="start"
                classes={{
                  dropdownContent: s.dropdownContentIconOnly,
                  dropdownContainer: s.dropdownContainer,
                  heading: s.headingIconOnly,
                  dropdownAnimationContainer: s.dropdownAnimationContainer,
                  dropdownLink: s.dropdownLinkIcon,
                  right: s.right,
                  onActive: { heading: s.headingActive }
                }}
                onSelect={val => setChain(+val)}
                title="Chain"
                items={mapNetworksToSelect()}
                selected={chain?.toString()}
                showSelectedIconOnly
              />
            </VStack>
            <VStack gap="8px" alignItems="flex-start">
              <DropdownMenu
                disabled
                iconPosition="start"
                classes={{
                  dropdownContent: s.dropdownContentIconOnly,
                  dropdownContainer: s.dropdownContainer,
                  heading: s.headingIconOnly,
                  dropdownAnimationContainer: s.dropdownAnimationContainer,
                  dropdownLink: s.dropdownLinkIcon,
                  right: s.right,
                  onActive: { heading: s.headingActive }
                }}
                onSelect={val => setDex(val)}
                title="Dex"
                items={[
                  {
                    title: 'KIM',
                    icon: <KimLogo key="0" fill="#FF4500" />,
                    value: 'kim'
                  }
                ]}
                selected={dex}
                showSelectedIconOnly
              />
            </VStack>
          </HStack>
          <HStack width="100%" flexWrap="wrap" paddingTop="16px" gap="16px">
            <DropdownMenu
              classes={{
                dropdownContent: s.dropdownContent,
                dropdownContainer: s.dropdownContainer,
                heading: s.heading,
                dropdownAnimationContainer: s.dropdownAnimationContainer,
                dropdownLink: s.dropdownLink,
                onActive: { heading: s.headingActive }
              }}
              onSelect={val => setVault(val)}
              title="Vaults"
              items={VAULT_OPTIONS}
              selected={vault}
            />
            <DropdownMenu
              classes={{
                dropdownContent: s.dropdownContent,
                dropdownContainer: s.dropdownContainer,
                heading: s.heading,
                dropdownAnimationContainer: s.dropdownAnimationContainer,
                dropdownLink: s.dropdownLink,
                onActive: { heading: s.headingActive }
              }}
              onSelect={val => setAuctionType(val)}
              title="Auction Type"
              items={AUCTION_TYPE_OPTIONS}
              selected={auctionType}
            />
            <DropdownMenu
              classes={{
                dropdownContent: s.dropdownContent,
                dropdownContainer: s.dropdownContainer,
                heading: s.heading,
                dropdownAnimationContainer: s.dropdownAnimationContainer,
                dropdownLink: s.dropdownLink,
                onActive: { heading: s.headingActive }
              }}
              onSelect={val => setAuctionStatus(val)}
              title="Auction Status"
              items={AUCTION_STATUS_OPTIONS}
              selected={auctionStatus}
            />
            <DropdownMenu
              classes={{
                dropdownContent: s.dropdownContent,
                dropdownContainer: s.dropdownContainer,
                heading: s.heading,
                dropdownAnimationContainer: s.dropdownAnimationContainer,
                dropdownLink: s.dropdownLink,
                onActive: { heading: s.headingActive }
              }}
              onSelect={val => {
                setSortOrder(val);
              }}
              items={PRICE_AND_LISTING_OPTIONS}
              selected={sortOrder}
            />
          </HStack>
        </VStack>
      ) : (
        <VStack width="100%" alignItems="flex-start">
          <HStack
            paddingTop="18px"
            alignItems="flex-end"
            gap="16px"
            mb={6}
            flexWrap="wrap"
          >
            <Box maxWidth={['unset', '100%']}>
              <SearchControlled
                onSearch={val => {
                  setActivePage(0);
                  setSearchTerm(val);
                }}
                value={searchTerm}
              />
            </Box>
            <VStack gap="8px" alignItems="flex-start">
              <DropdownMenu
                disabled
                iconPosition="start"
                classes={{
                  dropdownContent: s.dropdownContentIconOnly,
                  dropdownContainer: s.dropdownContainer,
                  heading: s.headingIconOnly,
                  dropdownAnimationContainer: s.dropdownAnimationContainer,
                  dropdownLink: s.dropdownLinkIcon,
                  right: s.right,
                  onActive: { heading: s.headingActive }
                }}
                onSelect={val => setChain(+val)}
                title="Chain"
                items={mapNetworksToSelect()}
                selected={chain?.toString()}
                showSelectedIconOnly
              />
            </VStack>
            <VStack gap="8px" alignItems="flex-start">
              <DropdownMenu
                disabled
                iconPosition="start"
                classes={{
                  dropdownContent: s.dropdownContentIconOnly,
                  dropdownContainer: s.dropdownContainer,
                  heading: s.headingIconOnly,
                  dropdownAnimationContainer: s.dropdownAnimationContainer,
                  dropdownLink: s.dropdownLinkIcon,
                  right: s.right,
                  onActive: { heading: s.headingActive }
                }}
                onSelect={val => setDex(val)}
                title="Dex"
                items={[
                  {
                    title: 'KIM',
                    icon: <KimLogo key="0" fill="#FF4500" />,
                    value: 'kim'
                  }
                ]}
                selected={dex}
                showSelectedIconOnly
              />
            </VStack>
            <VStack gap="8px" alignItems="flex-start">
              <DropdownMenu
                classes={{
                  dropdownContent: s.dropdownContent,
                  dropdownContainer: s.dropdownContainer,
                  heading: s.heading,
                  dropdownAnimationContainer: s.dropdownAnimationContainer,
                  dropdownLink: s.dropdownLink,
                  onActive: { heading: s.headingActive }
                }}
                onSelect={val => setVault(val)}
                title="Vaults"
                items={VAULT_OPTIONS}
                selected={vault}
              />
            </VStack>
            <VStack gap="8px" alignItems="flex-start">
              <DropdownMenu
                classes={{
                  dropdownContent: s.dropdownContent,
                  dropdownContainer: s.dropdownContainer,
                  heading: s.heading,
                  dropdownAnimationContainer: s.dropdownAnimationContainer,
                  dropdownLink: s.dropdownLink,
                  onActive: { heading: s.headingActive }
                }}
                onSelect={val => setAuctionType(val)}
                title="Auction Type"
                items={AUCTION_TYPE_OPTIONS}
                selected={auctionType}
              />
            </VStack>
            <VStack gap="8px" alignItems="flex-start">
              <DropdownMenu
                classes={{
                  dropdownContent: s.dropdownContent,
                  dropdownContainer: s.dropdownContainer,
                  heading: s.heading,
                  dropdownAnimationContainer: s.dropdownAnimationContainer,
                  dropdownLink: s.dropdownLink,
                  onActive: { heading: s.headingActive }
                }}
                onSelect={val => setAuctionStatus(val)}
                title="Auction Status"
                items={AUCTION_STATUS_OPTIONS}
                selected={auctionStatus}
              />
            </VStack>
            <VStack gap="8px" alignItems="flex-start">
              <DropdownMenu
                classes={{
                  dropdownContent: s.dropdownContent,
                  dropdownContainer: s.dropdownContainer,
                  heading: s.heading,
                  dropdownAnimationContainer: s.dropdownAnimationContainer,
                  dropdownLink: s.dropdownLink,
                  onActive: { heading: s.headingActive }
                }}
                onSelect={val => {
                  setSortOrder(val);
                }}
                items={PRICE_AND_LISTING_OPTIONS}
                selected={sortOrder}
              />
            </VStack>
          </HStack>
        </VStack>
      )}
    </>
  );
};
